import { Button, Text, Tooltip } from "@fluentui/react-components";
import { ChevronLeft16Regular, Settings16Regular } from "@fluentui/react-icons";
import {
  getDiagnosticById,
  getDistributionRespodents,
} from "apps/diagnostics/api";
import { IDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import { IDiagnosticDistribution } from "apps/diagnostics/models/IDiagnosticDistribution";
import { IObjective } from "apps/diagnostics/models/IObjective";
import { Constants, ConstantValues } from "common/constants";
import { LoadingSpinner } from "components/authguard/LoadingSpinner";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { SkeletonTable } from "components/skeletonTable/SkeletonTable";
import { IUserDetails } from "models/IUserDetails";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "store";
import { updateDiagnosticInSlice } from "../../../../redux/diagnosticsSlice";
import { DiagnosticsLayout } from "../DiagnosticsLayout";
import { EditDiagnostic } from "../manageDiagnostics/editDialog/EditDiagnostic";
import { ManageDistributions } from "../manageDistributions/ManageDistributions";
import { ManageObjectives } from "../manageObjectives/ManageObjectives";
import { useStyles } from "./manageDiagnostic.styles";

type TabType = "objectives" | "distributions";

export const ManageDiagnostic = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const styles = useStyles();

  const { id } = useParams<{ id: string }>();

  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const isDiagnosticsAdmin = !!currentUser?.roles?.includes(
    Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_VALUE,
  );

  const [activeTab, setActiveTab] = useState<TabType>("objectives");
  const [diagnostic, setDiagnostic] = useState<IDiagnostic | null>(null);
  const [respondents, setRespondents] = useState<Record<
    string,
    { users: IUserDetails[] }
  > | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEditDiagnosticPopupOpen, setIsEditDiagnosticPopupOpen] =
    useState(false);
  const dialogTriggerRef = React.useRef<HTMLElement | null>(null);

  const saveFocusAndRun = useCallback((fn: () => void) => {
    dialogTriggerRef.current = document.activeElement as HTMLElement;
    fn();
  }, []);

  const restoreFocus = useCallback(() => {
    dialogTriggerRef.current?.focus();
    dialogTriggerRef.current = null;
  }, []);

  const handleDistributionsChange = useCallback(
    (updatedDistributions: IDiagnosticDistribution[]) => {
      if (!diagnostic) return;

      const updated = { ...diagnostic, distributions: updatedDistributions };
      setDiagnostic(updated);
      dispatch(updateDiagnosticInSlice(updated));
    },
    [diagnostic, dispatch],
  );

  const handleObjectivesChange = useCallback(
    (updatedObjectives: IObjective[]) => {
      setDiagnostic((prev) =>
        prev ? { ...prev, objectives: updatedObjectives } : prev,
      );
    },
    [],
  );

  const [loadError, setLoadError] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const fetchDiagnostic = async () => {
    if (!id) {
      setIsLoading(false);
      setLoadError("No diagnostic id found in URL.");
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(ConstantValues.EMPTY_STRING);
      const result = await getDiagnosticById({ id });
      setDiagnostic(result);
      dispatch(updateDiagnosticInSlice(result));
    } catch (err) {
      setLoadError("Failed to load diagnostic.");
      console.error("Failed to fetch diagnostic by id:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostic();
  }, [id]);

  const refreshRespondents = useCallback(async () => {
    if (!id) return;

    const result: Record<string, { users: IUserDetails[] }> =
      await getDistributionRespodents({ id });
    setRespondents(result);
  }, [id]);

  useEffect(() => {
    let ignore: boolean = false;

    const fetchRespondents = async () => {
      if (!diagnostic || !diagnostic.id) return;

      try {
        const result: Record<string, { users: IUserDetails[] }> =
          await getDistributionRespodents({ id: diagnostic.id });
        if (!ignore) setRespondents(result);
      } catch (err) {
        console.error("Failed to fetch answer metadata:", err);
        if (!ignore) setRespondents({});
      }
    };

    fetchRespondents();
    return () => {
      ignore = true;
    };
  }, [diagnostic?.id]);

  if (isLoading) {
    return (
      <DiagnosticsLayout>
        <LoadingSpinner />
      </DiagnosticsLayout>
    );
  }

  if (!diagnostic) {
    return (
      <DiagnosticsLayout>
        <span className={styles.notFoundText}>Diagnostic not found.</span>
      </DiagnosticsLayout>
    );
  }

  const isReadyForObjectives = !!diagnostic && !isLoading && !loadError;
  const isReadyForDistributions = isReadyForObjectives && !!respondents;

  const objectiveCount = diagnostic.objectives?.length ?? 0;
  const respondentsTotal = Object.values(respondents ?? {}).reduce(
    (acc, x) => acc + (x.users?.length ?? 0),
    0,
  );

  return (
    <DiagnosticsLayout>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <Button
            appearance="outline"
            shape="rounded"
            icon={<ChevronLeft16Regular />}
            onClick={() => navigate("/diagnostics")}
            aria-label="Back to Diagnostics"
          />
          <Text size={500} weight="semibold" className={styles.title}>
            {diagnostic.name}
          </Text>
          <Tooltip
            content="Edit Diagnostic Configuration"
            relationship="description"
          >
            <Button
              appearance="outline"
              shape="rounded"
              icon={<Settings16Regular />}
              className={styles.configureButton}
              onClick={() =>
                saveFocusAndRun(() => setIsEditDiagnosticPopupOpen(true))
              }
            >
              Configure
            </Button>
          </Tooltip>
        </div>

        <div className={styles.sectionScrollArea}>
          <DialogTabs
            tabs={[
              { key: "objectives", label: "Objectives" },
              { key: "distributions", label: "Distributions" },
            ]}
            activeTab={activeTab}
            onTabClick={(tab) => setActiveTab(tab as TabType)}
          />

          <div className={styles.tabContentArea}>
            {activeTab === "objectives" &&
              (!isReadyForObjectives ? (
                <SkeletonTable fillContainer />
              ) : (
                <ManageObjectives
                  diagnosticId={diagnostic.id}
                  objectives={diagnostic.objectives}
                  onObjectivesChange={handleObjectivesChange}
                />
              ))}

            {activeTab === "distributions" &&
              (!isReadyForDistributions ? (
                <SkeletonTable fillContainer />
              ) : (
                <ManageDistributions
                  diagnosticId={diagnostic.id}
                  distributions={diagnostic.distributions}
                  objectives={diagnostic.objectives}
                  respondents={respondents}
                  onDistributionsChange={handleDistributionsChange}
                  onRefreshRespondents={refreshRespondents}
                />
              ))}
          </div>
        </div>
      </div>

      {isEditDiagnosticPopupOpen && (
        <EditDiagnostic
          isOpen={isEditDiagnosticPopupOpen}
          diagnostic={diagnostic}
          isDiagnosticsAdmin={isDiagnosticsAdmin}
          onDismiss={async (shouldRefresh: boolean) => {
            setIsEditDiagnosticPopupOpen(false);
            restoreFocus();
            if (shouldRefresh && id) {
              try {
                const result = await getDiagnosticById({ id });
                setDiagnostic(result);
                dispatch(updateDiagnosticInSlice(result));
              } catch (err) {
                console.error("Failed to refresh diagnostic after save:", err);
              }
            }
          }}
        />
      )}
    </DiagnosticsLayout>
  );
};
